import {ChangeDetectionStrategy, Component, OnInit, ViewChild} from '@angular/core';
import {Actions, ofActionSuccessful, Select, Store} from '@ngxs/store';
import {ReportsState} from '../../../../shared/states/reports/reports.state';
import {Observable} from 'rxjs/internal/Observable';
import {IDeaRecord, IDeaReportRecord, IReportPackagingDetail} from '../../../../shared/interfaces/dea-record.interface';
import {
  GetReportById,
  SaveDeaReportDetailsRecord,
  SetDeaReportDetailsList,
  SetLoading,
  SignReport as SignReport,
} from '../../../../shared/states/reports/reports.actions';
import {PharmacyState} from '../../../../shared/states/pharmacy/pharmacy.state';
import {AuthState} from '../../../../shared/states/auth/auth.state';
import {IUser} from '../../../../shared/interfaces/user.interface';
import {BaseTableDirective} from '../../../../shared/directives/base-table.directive';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActivatedRoute} from '@angular/router';
import {REPORT_TIME_LIST} from '../constants/constants';
import {ReportsService} from 'src/app/reports/reports.service';
import {take} from 'rxjs/operators';
import {IAlertMessage} from '../../../../shared/interfaces/alert-message.interface';
import {AlertTypesEnum} from '../../../../shared/interfaces/AlertTypesEnum';
import {InputComponent} from '../../../../shared/components/input/input.component';
import {ButtonComponent} from '../../../../shared/components/button/button.component';
import Decimal from 'decimal.js';
import { getFlatObject, groupByProperty } from 'src/app/shared/helpers/helpers';
import { DrugScheduleEnum } from 'src/app/shared/interfaces/DrugScheduleEnum ';
import { ISignerWitnessPinSignatureCollectedEvent } from 'src/app/shared/interfaces/singer-witness-pin-signature-collected.event';
import { GroupedBy } from 'src/app/shared/interfaces/grouped-by.enum';
import { getGroupedBySimilarGroupHeader } from 'src/app/reports/helpers';

@UntilDestroy()
@Component({
  selector: 'app-report-details-list',
  templateUrl: './dea-report-details.component.html',
  styleUrls: ['./dea-report-details.component.scss']
})
export class DeaReportDetailsComponent extends BaseTableDirective implements OnInit {
  collectSignaturesAndPinsDialog = false;

  isFileDownloading: boolean;

  tableColumns = {
    friendlyName: true,
    locationName: true,
  };

  today = new Date();
  reportTime: string;

  editRowOptions: {
    isEdit: boolean,
    fullPkg: number,
    partialPackages: number,
    transactionID: string,
    partialPackageQuantity: number }[] = [];

  report: IDeaRecord | undefined;
  originalReport: IDeaRecord | undefined;

  showMessage: boolean;
  autohideDelay = 3;
  alertMessage: IAlertMessage = {
    type: AlertTypesEnum.ErrorInfo,
    text: '',
  };

  locationsString: string;
  groupResultsBy: GroupedBy = GroupedBy.Drug;
  groupItemsOpened: boolean[] = [];
  isGroupsMode: boolean;
  flatReportDetails: IDeaReportRecord[];

  GroupedBy = GroupedBy;

  @Select(ReportsState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(ReportsState.currentReport) currentReport$: Observable<any>;
  @Select(PharmacyState.currentPharmacy) currentPharmacy$: Observable<any>;
  @Select(PharmacyState.pharmacyData) pharmacyData$: Observable<any>;
  @Select(AuthState.activeUser) currentUser$: Observable<IUser>;
  @Select(ReportsState.newReportData) newReportData$: Observable<any>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;

  @ViewChild('pinUser') pinUserDialog;
  @ViewChild('pinWitness') pinWitnessDialog;
  @ViewChild('fullPkgInput') fullPkgInput: InputComponent;
  @ViewChild('openPkgInput') openPkgInput: InputComponent;
  @ViewChild('closeBtn') closeBtn: ButtonComponent;
  @ViewChild('saveBtn') saveBtn: ButtonComponent;

  get isSigned(): boolean {
    return (this.report?.signerFirstName + this.report?.signerLastName)?.length > 0;
  }

  DrugScheduleEnum = DrugScheduleEnum;

  constructor(
    protected store: Store,
    private route: ActivatedRoute,
    private reportsService: ReportsService,
    private actions$: Actions
  ) {
    super(store);
    const reportId = this.route.snapshot.params?.id;

    if (reportId !== undefined) {
      this.store.dispatch(new GetReportById(reportId));
    } else {
      this.newReportData$.pipe(untilDestroyed(this))
        .subscribe((data: any) => {
          if (data?.reportTime >= 0) {
            this.reportTime = REPORT_TIME_LIST.find(el => el.id === data.reportTime)?.name;
          }
        });
    }
  }

  ngOnInit(): void {
    this.currentReport$
      .pipe(untilDestroyed(this))
      .subscribe((data: any) => {
        this.report = {...data};
        this.flatReportDetails = this.report.details;
        this.originalReport = {...data};
        if (this.report?.details && this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
          this.initLocationsString(data);
          this.setGroups(this.report);
          this.isGroupsMode = true;
        }
        this.setupEditRowOptions();

        if (this.report && this.report.id) {
          this.reportTime = REPORT_TIME_LIST.find(el => el.id === data.timeType)?.name;
        }
      });

    this.actions$.pipe(ofActionSuccessful(SaveDeaReportDetailsRecord))
      .subscribe(() => {
        this.setupEditRowOptions();
      })
  }

  setupEditRowOptions(): void {
    if (!this.report?.details) {
      this.editRowOptions = [];
    } else {
      const toAdd = (item: IDeaReportRecord) => {
        this.editRowOptions.push({
          isEdit: false,
          fullPkg: item.fullPackages || 0,
          partialPackages: item.partialPackages || 0,
          transactionID: item.transactionID,
          partialPackageQuantity: item.partialPackageQuantity,
        });
      };
      if (this.report.details?.length) {
        this.editRowOptions = [];
        if (!this.isGroupsMode) {
          this.report.details.forEach(toAdd);
        }
        else {
          this.report.details.forEach((group: any) => {
            group?.entries?.forEach(toAdd);
          });
        }
      }
    }
  }

  signReport(): void {
    if (!this.isSigned) {
      this.collectSignaturesAndPinsDialog = true;
    }
  }

  handleDownload(res: Blob, filetype: string): void {
    const downloadedFile = new Blob([res], {type: res.type});
    const a = document.createElement('a');
    a.setAttribute('style', 'display:none;');
    document.body.appendChild(a);
    a.download = `${this.report.title}_ ${new Date().toDateString()}.${filetype}`;
    a.href = URL.createObjectURL(downloadedFile);
    a.target = '_blank';
    a.click();
    document.body.removeChild(a);
    this.store.dispatch(new SetLoading(false));
    this.isFileDownloading = false;
  }

  savePDF(): void {
    this.reportsService.downloadDeaPdfReport(this.report.id, 'friendlyName', this.tableColumns.friendlyName, this.isGroupsMode, this.groupResultsBy)
      .pipe(take(1))
      .subscribe(res => {
        this.handleDownload(res, 'pdf');
      }, err => {
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      });
  }

  saveCSV(): void {
    this.reportsService.downloadDeaCsvReport(this.report.id, 'friendlyName', this.tableColumns.friendlyName, this.isGroupsMode, this.groupResultsBy)
      .pipe(take(1))
      .subscribe(res => {
        this.handleDownload(res, 'csv');
      }, err => {
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      });
  }

  sortByColumn(columnName: string): void {
    if (this.isGroupsMode) {
      this.store.dispatch(new SetDeaReportDetailsList(this.sort(this.flatReportDetails, columnName)));
    } else {
      this.store.dispatch(new SetDeaReportDetailsList(this.sort(this.report.details, columnName)));
    }
    this.setupEditRowOptions();
  }

  afterSignatureAndPinCollectionHandled(event: ISignerWitnessPinSignatureCollectedEvent): void {
    this.collectSignaturesAndPinsDialog = false;
    this.store.dispatch(new SignReport(this.report.id, event.base64SignerSignature, +event.signerPin, event.base64WitnessSignature, +event.witnessPin, this.tableColumns.friendlyName, this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)));
  }

  setDetailsRowEditMode(i: number): void {
    this.editRowOptions.forEach(el => el.isEdit = false);
    this.editRowOptions[i].isEdit = true;
  }

  cancelDetailsRowChanges(i: number): void {
    this.editRowOptions[i].isEdit = false;
    this.editRowOptions[i].fullPkg = this.flatReportDetails[i].fullPackages || 0;
    this.editRowOptions[i].partialPackages = this.flatReportDetails[i].partialPackages || 0;
    this.editRowOptions[i].partialPackageQuantity = this.flatReportDetails[i].partialPackageQuantity || 0;
  }

  saveDetailsRowChanges(i: number): void {

    if (this.isFullPkgInputInvalid(i)) {
      this.showMessage = true;
      this.alertMessage.text = 'Must not have more full packages than total inventory amount';
      return;
    } 
    if (this.isOpenPkgInputMaxInvalid(i)) {
      this.showMessage = true;
      this.alertMessage.text = 'Must not have more open packages than quantity in open packages';
      return;
    }

    if (this.isOpenPkgInputMinInvalid(i)) {
      this.showMessage = true;
      this.alertMessage.text = 'Must not have less open packages than quantity in open packages';
      return;
    }

    if (this.isFullPkgInputMinInvalid(i) || +this.editRowOptions[i].partialPackages < 0) {
      this.showMessage = true;
      this.alertMessage.text = 'Must not have value less than 0';
      return;
    }

    this.editRowOptions[i].isEdit = false;

    const detail: IReportPackagingDetail = {
      reportId: this.report.id,
      fullPackages: +this.editRowOptions[i].fullPkg,
      partialPackages: +this.editRowOptions[i].partialPackages,
      partialPackageQuantity: +this.editRowOptions[i].partialPackageQuantity,
      userID: this.store.selectSnapshot(AuthState.activeUserId),
      transactionID: this.editRowOptions[i].transactionID
    }

    this.store.dispatch(new SaveDeaReportDetailsRecord(i, detail));
  }

  validateFullPkgInput(i: number): boolean {
    return this.isFullPkgInputInvalid(i) || this.isFullPkgInputMinInvalid(i)
  }

  validateOpenPkgInput(i: number): boolean {
    return this.isOpenPkgInputMaxInvalid(i) || this.isOpenPkgInputMinInvalid(i);
  }

  isFullPkgInputInvalid(i: number): boolean {
    return (this.editRowOptions[i].fullPkg * this.flatReportDetails[i].packageQuantity) > this.flatReportDetails[i].total;
  }

  isFullPkgInputMinInvalid(i: number): boolean {
    return this.editRowOptions[i].fullPkg < 0;
  }

  isOpenPkgInputMaxInvalid(i: number): boolean {
    //Roland introduced 5x upper limit
    return this.editRowOptions[i].partialPackages > (this.getOpenPkgPossibleValue(i) * 5);
  }

  isOpenPkgInputMinInvalid(i: number): boolean {
    return this.getOpenPkgPossibleValue(i) > this.editRowOptions[i].partialPackages;
  }

  getOpenPkgPossibleValue(i: number): number {
    return Math.ceil(this.editRowOptions[i].partialPackageQuantity / this.flatReportDetails[i].packageQuantity);
  }


  trackDeaRecordsById(index: number, item: IDeaReportRecord): string {
    return item.drugID;
  }

  focusButton(buttonName: string): void {
    if (this[buttonName]) {
      this[buttonName].focus();
    }
  }


  onPackagingChange(i: number): void {
    const row = this.report?.details[i];
    if (row) {
      var fullPackageQuantity = this.editRowOptions[i].fullPkg * row.packageQuantity;
      const partialQty = new Decimal(row.total).sub(fullPackageQuantity).toNumber();

      this.editRowOptions[i].partialPackageQuantity = partialQty;
    }
  }

  onPackagingChangeInGroup(groupIdex: number, detailInGroupIndex: number, flatArrayItemIndex: number): void {
    const row = (<any>this.report?.details[groupIdex])?.entries[detailInGroupIndex];
    if (row) {
      const editRowOption = this.editRowOptions[flatArrayItemIndex];
      var fullPackageQuantity = editRowOption.fullPkg * row.packageQuantity;
      const partialQty = new Decimal(row.total).sub(fullPackageQuantity).toNumber();

      editRowOption.partialPackageQuantity = partialQty;
    }
  }

  private initLocationsString(report: IDeaRecord): void {
    this.locationsString =  [...new Set(this.report?.details?.map(x => x.locationName))].join(', ');
  }

  private setGroups(report: IDeaRecord): void {
    const flatList = [...report?.details].map((detail: IDeaReportRecord) => {
      const flatTransaction = getFlatObject({...detail, history: null});
      switch (this.groupResultsBy) {
        case GroupedBy.Drug:
          flatTransaction.drug = `${flatTransaction['friendlyName']} (${flatTransaction['packageNdc11Digits']})`;
          break;
      }
      return flatTransaction;
    });

    switch (this.groupResultsBy) {
      case GroupedBy.Drug:
        this.report.details = this.groupByProperty(flatList, 'drug', (_, gKey) => gKey);
        break;
      case GroupedBy.GPI:
        this.report.details = this.groupByProperty(flatList, 'gpi', (group) => getGroupedBySimilarGroupHeader(group));
        break;
    }
    this.groupItemsOpened = Array(this.report.details.length).fill(true);  
  }

  private groupByProperty(array: any[], key: string, headerExtractor: (group: any[], groupKey: string) => string): any[] {
    const sortObjByProperty = array.reduce((rv, x, index) => {
      (rv[x[key]] = rv[x[key]] || []).push({...x, flatArrayItemIndex: index}); //index in flat array
      return rv;
    }, {});
    const sumReducer = (accumulator, currentValue) => new Decimal(+accumulator).add(new Decimal(+currentValue));
    const result = Object.entries(sortObjByProperty).map(([k, v]) => ({
      header: headerExtractor(v as any[], k),
      entries: v, 
      totalPerGroup: (v as any[])?.map(x => x?.total)?.reduce(sumReducer),
      openQtyTotalPerGroup: (v as any[])?.map(x => x?.partialPackageQuantity)?.reduce(sumReducer),
      openPkgTotalPerGroup: (v as any[])?.map(x => x?.partialPackages)?.reduce(sumReducer),
      fullPkgTotalPerGroup: (v as any[])?.map(x => x?.fullPackages)?.reduce(sumReducer),
    }));

    result.sort((a, b) => {
      if (a.header === 'N/A' && b.header !== 'N/A') {
          return 1;
      } else if (a.header !== 'N/A' && b.header === 'N/A') {
          return -1;
      } else {
          return 0;
      }
    });
    return result;
  }
  

  switchFilterResultBy(groupedBy: GroupedBy): void {
    this.groupResultsBy = groupedBy;
    this.setGroups(this.originalReport);
  }

}
